// Live-control action rendering. The orchestration hook owns payloads and one-shot fencing; this file owns
// only explicit interaction states, including provider-live's bound second confirmation and immediate Stop.

import type { JourneyLiveArmMode, JourneyLivePreflightView } from '../journey-live-api.ts';
import { journeyLiveModeName } from '../journey-live-view.ts';
import type { JourneyLiveController } from '../useJourneyLive.ts';
import { Btn } from '../primitives.tsx';

function ProviderLiveConfirmation({
  busy,
  onCancel,
  onConfirm,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label="Confirm provider-backed test"
      className="mt-4 border-l-2 border-clay bg-surface-sunken p-4"
    >
      <p className="text-sm font-semibold text-text">External provider access will be enabled.</p>
      <p className="mt-1 text-sm text-text-muted">
        This permits the served Ritona slice to call only the pinned model, controlled mailbox and
        synthetic HubSpot portal within the server-enforced budgets and allowlists.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Btn kind="quiet" sm disabled={busy} onClick={onCancel}>
          Cancel
        </Btn>
        <Btn sm disabled={busy} onClick={onConfirm}>
          {busy ? 'Arming…' : 'Confirm provider-backed test'}
        </Btn>
      </div>
    </div>
  );
}

function ProviderLiveActions({
  live,
  preflight,
  confirmationFor,
  setConfirmationFor,
}: {
  readonly live: JourneyLiveController;
  readonly preflight: JourneyLivePreflightView;
  readonly confirmationFor: string | null;
  readonly setConfirmationFor: (value: string | null) => void;
}): React.JSX.Element {
  const busy = live.operation !== null;
  if (confirmationFor === preflight.id) {
    return (
      <ProviderLiveConfirmation
        busy={busy}
        onCancel={() => setConfirmationFor(null)}
        onConfirm={() => void live.arm('provider_live')}
      />
    );
  }
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <Btn disabled={busy} onClick={() => setConfirmationFor(preflight.id)}>
        Review provider-backed test
      </Btn>
      <Btn kind="quiet" sm disabled={busy} onClick={() => void live.preflight('provider_live')}>
        Run preflight again
      </Btn>
    </div>
  );
}

function PreflightActions({
  live,
  mode,
  disabled,
}: {
  readonly live: JourneyLiveController;
  readonly mode: JourneyLiveArmMode;
  readonly disabled: boolean;
}): React.JSX.Element {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <Btn disabled={disabled} onClick={() => void live.preflight(mode)}>
        {live.operation === 'preflight'
          ? 'Checking…'
          : `Run ${journeyLiveModeName(mode)} preflight`}
      </Btn>
      <Btn kind="quiet" sm disabled={live.operation !== null} onClick={() => void live.refresh()}>
        Refresh status
      </Btn>
    </div>
  );
}

function RehearsalArmActions({
  live,
}: {
  readonly live: JourneyLiveController;
}): React.JSX.Element {
  const busy = live.operation !== null;
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <Btn disabled={busy} onClick={() => void live.arm('no_egress_rehearsal')}>
        {live.operation === 'arm' ? 'Arming…' : 'Arm full-flow rehearsal'}
      </Btn>
      <Btn
        kind="quiet"
        sm
        disabled={busy}
        onClick={() => void live.preflight('no_egress_rehearsal')}
      >
        Run preflight again
      </Btn>
    </div>
  );
}

export function JourneyLiveControlActions({
  live,
  selectedMode,
  confirmationFor,
  setConfirmationFor,
}: {
  readonly live: JourneyLiveController;
  readonly selectedMode: JourneyLiveArmMode;
  readonly confirmationFor: string | null;
  readonly setConfirmationFor: (value: string | null) => void;
}): React.JSX.Element {
  const status = live.status;
  if (status === null) return <StatusRetry live={live} />;
  if (status.control.state === 'armed') return <ImmediateStop live={live} />;
  const preflight = status.latestPreflight;
  const armable = preflight?.pass === true && preflight.mode === selectedMode;
  if (!armable || preflight === undefined) {
    const disabled =
      live.operation !== null ||
      !status.configured ||
      (selectedMode === 'provider_live' && !status.providerLiveReady);
    return <PreflightActions live={live} mode={selectedMode} disabled={disabled} />;
  }
  if (selectedMode === 'provider_live') {
    return (
      <ProviderLiveActions
        live={live}
        preflight={preflight}
        confirmationFor={confirmationFor}
        setConfirmationFor={setConfirmationFor}
      />
    );
  }
  return <RehearsalArmActions live={live} />;
}

function StatusRetry({ live }: { readonly live: JourneyLiveController }): React.JSX.Element {
  return (
    <div className="mt-4">
      <Btn kind="quiet" sm disabled={live.loading} onClick={() => void live.refresh()}>
        {live.loading ? 'Loading…' : 'Retry status'}
      </Btn>
    </div>
  );
}

function ImmediateStop({ live }: { readonly live: JourneyLiveController }): React.JSX.Element {
  return (
    <div className="mt-5 border-t border-clay pt-4">
      <p className="mb-3 text-sm text-text-muted">
        Stop revokes the full-flow control lease before the active run is interrupted.
      </p>
      <Btn disabled={live.operation !== null} onClick={() => void live.stop()}>
        {live.operation === 'stop' ? 'Stopping…' : 'Stop full-flow execution now'}
      </Btn>
    </div>
  );
}
